import { useEffect } from 'react';
import { useCustomFieldValues } from '@/hooks/custom-fields/useCustomFieldValues';
import { resolveInheritedDraftValues } from '@/components/organisms/custom-fields/custom-field-form-utils';
import type { CustomFieldSchema } from '@/types/custom-fields';

interface UseCopyOnCreateInheritanceArgs {
  /** Only run while creating a new target (COPY is a one-shot seed, never on edit). */
  enabled: boolean;
  /** The target entity's custom-field schemas (carry the inheritance rules). */
  targetSchemas: CustomFieldSchema[];
  /** Selected source entity ids. Missing/empty ids simply don't contribute values. */
  sourceIds: { contact?: string | null; address?: string | null; job?: string | null };
  /**
   * Merge the resolved inherited draft values into the target's draft. Must respect
   * user overrides (see `applyInheritedDraftValues`) and be stable (useCallback).
   */
  apply: (inherited: Record<string, string>) => void;
}

/**
 * COPY_ON_CREATE inheritance for a create form: fetch the selected sources' custom
 * field values, resolve which target fields inherit (per the schema's rules), and
 * apply them as an editable pre-fill. Re-resolves when a source changes so a switched
 * contact/address updates the (untouched) inherited fields.
 */
export function useCopyOnCreateInheritance({
  enabled,
  targetSchemas,
  sourceIds,
  apply,
}: UseCopyOnCreateInheritanceArgs) {
  const active = enabled && targetSchemas.length > 0;
  const contact = useCustomFieldValues('contact', active ? (sourceIds.contact ?? '') : '');
  const address = useCustomFieldValues('address', active ? (sourceIds.address ?? '') : '');
  const job = useCustomFieldValues('job', active ? (sourceIds.job ?? '') : '');

  useEffect(() => {
    if (!active) return;
    const inherited = resolveInheritedDraftValues(targetSchemas, {
      contact: contact.data,
      address: address.data,
      job: job.data,
    });
    apply(inherited);
  }, [active, targetSchemas, contact.data, address.data, job.data, apply]);
}
